<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="invoice-details-header mb-4">
        <button type="button" class="btn invoice-back-button" :aria-label="$t('back')" @click="$router.go(-1)">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left" aria-hidden="true" /><span>{{$t('back')}}</span>
        </button>
        <div class="invoice-title-row d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
          <h3 class="invoice-reference mb-0">{{ reception.reference }}</h3>
          <div class="invoice-actions" role="toolbar" :aria-label="$t('actions')">
            <button v-if="reception.status != 'draft'" type="button" class="btn invoice-action-button invoice-action-button--primary" @click="ViewReception()">
              <AppIcon name="bi-file-earmark-pdf" class="bi bi-file-earmark-pdf" aria-hidden="true" /><span>{{$t('view')}}</span>
            </button>
            <button v-if="helper.checkAccess('edit_receptions') != -1" type="button" class="btn invoice-action-button invoice-action-button--secondary" @click="editRow()">
              <AppIcon name="bi-pencil" class="bi bi-pencil" aria-hidden="true" /><span>{{$t('edit')}}</span>
            </button>
            <div v-if="reception.status != 'draft' || helper.checkAccess('delete_receptions') != -1" class="btn-group invoice-action-group">
              <button id="receptionMoreActionsButton" class="btn invoice-action-button invoice-action-button--secondary invoice-action-button--icon dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" :aria-label="$t('more')" :title="$t('more')"><AppIcon name="bi-three-dots" class="bi bi-three-dots" aria-hidden="true" /></button>
              <ul class="dropdown-menu dropdown-menu-end invoice-actions-menu" aria-labelledby="receptionMoreActionsButton">
                <li v-if="reception.status != 'draft'"><button type="button" class="dropdown-item" @click="toggleReorder"><AppIcon name="bi-arrow-down-up" class="bi bi-arrow-down-up" /><span>{{$t('reorder')}}</span></button></li>
                <li v-if="reception.status != 'draft' && helper.checkAccess('delete_receptions') != -1"><hr class="dropdown-divider"></li>
                <li v-if="helper.checkAccess('delete_receptions') != -1"><button type="button" class="dropdown-item invoice-menu-action--danger" @click="deleteRow()"><AppIcon name="bi-trash" class="bi bi-trash" /><span>{{$t('delete')}}</span></button></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Reception Info -->
      <div v-else-if="reception && reception.id">
        <!-- Tabs -->
        <ul class="nav nav-tabs detail-page-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'overview'}" @click="step = 'overview'">{{$t('overview')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_attachments') != -1">
            <a class="nav-link" :class="{active: step === 'attachments'}" @click="step = 'attachments'">{{$t('attachments')}}</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'history'}" @click="step = 'history'">{{$t('history')}}</a>
          </li>
        </ul>

        <!-- Tab Content -->
        <!-- Overview Tab -->
        <div v-if="step === 'overview'">
          <div class="row document-overview-grid">
            <!-- Left Column: Reception Details -->
            <div class="col-md-8 document-overview-main">
              <div class="card mb-4 document-pdf-card">
                <div class="card-body p-0">
                  <invoice-template-modal v-if="reception && reception.id" inline :data="reception" type="reception" />
                </div>
              </div>
              <!-- Products Card -->
              <div v-if="reorderMode" class="card mb-4 document-products-card">
                <div class="card-header bg-white">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0"><AppIcon name="bi-box-seam" class="bi bi-box-seam me-2" />{{$t('products')}}</h6>
                    <div class="product-reorder-actions">
                        <div v-if="reorderMode" class="d-flex flex-wrap gap-2" role="group" :aria-label="$t('reorder')">
                            <button type="button" class="btn product-action-button product-action-button--save" @click="saveOrder">
                                <AppIcon name="bi-check-lg" class="bi bi-check-lg" aria-hidden="true" /> {{$t('save')}}
                            </button>
                            <button type="button" class="btn product-action-button" @click="toggleReorder">
                                <AppIcon name="bi-x-lg" class="bi bi-x-lg" aria-hidden="true" /> {{$t('cancel')}}
                            </button>
                        </div>
                    </div>
                  </div>
                </div>
                <div class="card-body table-responsive">
                  <table class="table mb-0 table-striped small">
                    <thead>
                      <tr class="text-secondary">
                        <th>{{$t('product_label')}}</th>
                        <th>{{$t('quantity')}}</th>
                        <th v-if="reorderMode" style="width: 100px;">{{$t('actions')}}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(product, index) in reception.products" :key="product.id">
                        <td><div class="product-rich-text" v-html="sanitizeRichText(product.product_label)"></div></td>
                        <td>{{product.quantity}}</td>
                        <td v-if="reorderMode">
                            <div class="d-flex gap-1">
                                <button type="button" class="btn product-move-button" @click="moveUp(index)" :disabled="index === 0"><AppIcon name="bi-arrow-up" class="bi bi-arrow-up" /></button>
                                <button type="button" class="btn product-move-button" @click="moveDown(index)" :disabled="index === reception.products.length - 1"><AppIcon name="bi-arrow-down" class="bi bi-arrow-down" /></button>
                            </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Notes Card -->
              <div v-if="reception.notes" class="card document-notes-card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-sticky" class="bi bi-sticky me-2" />{{$t('notes')}}</h6>
                </div>
                <div class="card-body">
                  <p class="mb-0">{{ reception.notes }}</p>
                </div>
              </div>
            </div>

            <!-- Right Column: Supplier Info -->
            <div class="col-md-4 document-overview-sidebar">
              <div class="card mb-4 document-info-card">
                <div class="card-header bg-white"><h6 class="mb-0"><AppIcon name="bi-inbox" class="bi bi-inbox me-2" />{{$t('reception')}} {{$t('information')}}</h6></div>
                <div class="card-body"><div class="row">
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('reference')}}</small><strong>{{reception.reference}}</strong></div>
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('order_date')}}</small><strong>{{formatDate(reception.date)}}</strong></div>
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('created_by')}}</small><strong>{{reception.user ? reception.user.name : '-'}}</strong></div>
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('created_at')}}</small><strong>{{formatDate(reception.created_at)}}</strong></div>
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('updated_at')}}</small><strong>{{formatDate(reception.updated_at)}}</strong></div>
                </div></div>
              </div>
              <div class="card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-building" class="bi bi-building me-2" />{{$t('supplier')}}</h6>
                </div>
                <div class="card-body">
                  <div v-if="!reception.supplier" class="text-muted">{{$t('no_supplier_found')}}</div>
                  <div v-else>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('reference')}}</small>
                      <strong>{{reception?.supplier?.reference}}</strong>
                    </div>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('name')}}</small>
                      <strong>{{reception?.supplier?.name}}</strong>
                    </div>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('phone')}}</small>
                      <strong>{{reception?.supplier?.phone || '-'}}</strong>
                    </div>
                    <div>
                      <small class="text-muted d-block">{{$t('email')}}</small>
                      <strong>{{reception?.supplier?.email || '-'}}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Other Tabs -->
        <div v-if="step === 'attachments' && helper.checkAccess('view_attachments') != -1">
          <attachments v-if="reception && reception.id" :entity_id="reception.id" entity_type="reception" />
        </div>

        <div v-if="step === 'history'">
          <document-history
            :history="history"
            :loading="loadingHistory"
            :pagination="historyPagination"
            @pageChange="changeHistoryPage"
          />
        </div>
      </div>

        <!-- quote modal -->
        <invoice-template-modal 
            v-if="invoiceModalReceptionIsOpen" 
            @modalClosed="invoiceModalReceptionIsOpen = false" 
            modalId="invoice-reception-modal-01" 
            :isModalOpen="invoiceModalReceptionIsOpen"  
            :data="reception" 
            type="reception">
        </invoice-template-modal>
        <!-- quote modal -->

        <!-- Edit Modal -->
        <add-edit-reception-modal 
            v-if="editModalReceptionisOpen" 
            @modalClosed="editModalReceptionisOpen = false" 
            modalId="edit-reception-modal-01" 
            :isModalOpen="editModalReceptionisOpen"  
            :data="reception" 
            action="update" 
            @refresh="getReception($route.params.id)"
        ></add-edit-reception-modal>
        <!-- Edit modal -->

    </div>
  </div>
</template>

<script>
import helper from '../../helper'
import moment from 'moment'
import attachments from '../../components/Attachment.vue'
import DocumentHistory from '../../components/DocumentHistory.vue';
import InvoiceTemplateModal from '../../components/invoiceLayout/InvoiceTemplateModal';
import AddEditReceptionModal from '../../components/reception/AddEditReceptionModal'

export default {
  name: 'ReceptionDetails',
  components :{
    attachments,
    DocumentHistory,
    InvoiceTemplateModal,
    AddEditReceptionModal
  },
  data(){
    return {
        loading : false,
        loadingHistory : false,
        reception : {},
        history : [],
        historyPagination : {
            count: 0,
            per_page: 10,
            current_page: 1,
            total_pages: 1
        },
        amount_paid : 0,
        total_purchases : 0,
        total_quote : 0,
        invoiceModalReceptionIsOpen : false,
        invoiceModalReception : null,
        editModalReception : null,
        editModalReceptionisOpen : false,
        createInvoiceModalState : false,
        createProductState : false,
        step : 'overview',
        reorderMode: false,
        originalProductsOrder: null
    }
  },
  mounted(){
        window.mixpanel.track("reception_details");

        //check permissions
        if(helper.checkAccess('view_receptions') == -1) return this.$router.push({path : '/'})
        if(helper.checkAccess('view_receptions') == -2) return this.$router.push({path : '/subscribe'})
  },
  created(){
    if(!this.$route.params || !this.$route.params.id) return this.$route.push('/receptions')
    this.getReception(this.$route.params.id)
    this.getReceptionHistory(this.$route.params.id)
  },
  methods : {
        sanitizeRichText(value){
            return helper.sanitizeRichText(value)
        },
        async getReception(id){
            try{
                this.loading = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/receptions/${id}`)
                this.reception = response.data
                this.loading = false
                
            }catch(error){
                this.$router.push({path : '/receptions'})
                this.loading = false
            }
        },
        async getReceptionHistory(id){
            try{
                this.loadingHistory = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/receptions/${id}/history`)
                this.history = response.data.data || response.data
                this.historyPagination = {
                    count: response.data.count || 0,
                    per_page: response.data.per_page || 10,
                    current_page: response.data.current_page || 1,
                    total_pages: response.data.total_pages || 1
                }
                this.loadingHistory = false
            }catch(error){
                alert('Error fetching history:', error)
                this.loadingHistory = false
            }
        },
        getActionLabel(action){
            const labels = {
                'add': this.$t('created'),
                'edit': this.$t('modified'),
                'delete': this.$t('deleted')
            }
            return labels[action] || action
        },
        getActionBadgeClass(action){
            const classes = {
                'add': 'bg-success',
                'edit': 'bg-warning',
                'delete': 'bg-danger'
            }
            return classes[action] || 'bg-secondary'
        },
        async changeHistoryPage(page){
            if(page < 1 || page > this.historyPagination.total_pages) return;
            
            try{
                this.loadingHistory = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/receptions/${this.reception.id}/history?page=${page}&per_page=${this.historyPagination.per_page}`)
                this.history = response.data.data || response.data
                this.historyPagination = {
                    count: response.data.count || 0,
                    per_page: response.data.per_page || 10,
                    current_page: response.data.current_page || 1,
                    total_pages: response.data.total_pages || 1
                }
                this.loadingHistory = false
            }catch(error){
                alert('Error fetching history page:', error)
                this.loadingHistory = false
            }
        },
        getPageNumbers(){
            const pages = [];
            const current = this.historyPagination.current_page;
            const total = this.historyPagination.total_pages;
            
            // Show up to 5 pages around current page
            let start = Math.max(1, current - 2);
            let end = Math.min(total, current + 2);
            
            // Adjust if we're near the beginning or end
            if(end - start < 4){
                if(start === 1){
                    end = Math.min(total, start + 4);
                } else {
                    start = Math.max(1, end - 4);
                }
            }
            
            for(let i = start; i <= end; i++){
                pages.push(i);
            }
            
            return pages;
        },
        editRow(){
            this.editModalReceptionisOpen = true;
            this.reception.date = moment(this.reception.date).format('YYYY-MM-DD')
        }, 
        ViewReception(){
            this.invoiceModalReceptionIsOpen = true;
        },
        async deleteRow(){
            this.$swal.fire({
                title: `${this.$t('are_you_sure')}?`,
                text : this.$t('you_cant_revert'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: this.$t('yes_delete_it')
            })
            .then(async (result) => {
                if (result.isConfirmed) {
                    await helper.customDelete(`${process.env.VUE_APP_API}/receptions/${this.reception.id}`)
                    this.$router.push({path : '/receptions'})
                }
            })
        },
      
        transferInvoice(){

            this.reception.total = this.reception.products && this.reception.products.length ? this.reception.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0;

            this.createInvoiceModalState = !this.createInvoiceModalState

        },
        formatReference(number,id) { return helper.formatReference(number,id);},
        formatDate(date) { return helper.formatDate(date) },
        formatMoney(number) { return helper.formatMoney(number);},
        toggleReorder() {
            if (!this.reorderMode) {
                // Enter reorder mode: save current order to restore if cancelled
                this.originalProductsOrder = JSON.parse(JSON.stringify(this.reception.products));
                this.reorderMode = true;
            } else {
                // Cancel reorder mode: restore original order
                this.reception.products = this.originalProductsOrder;
                this.reorderMode = false;
                this.originalProductsOrder = null;
            }
        },
        moveUp(index) {
            if (index > 0) {
                const products = [...this.reception.products];
                const temp = products[index];
                products[index] = products[index - 1];
                products[index - 1] = temp;
                this.reception.products = products;
            }
        },
        moveDown(index) {
            if (index < this.reception.products.length - 1) {
                const products = [...this.reception.products];
                const temp = products[index];
                products[index] = products[index + 1];
                products[index + 1] = temp;
                this.reception.products = products;
            }
        },
        async saveOrder() {
            try {
                this.loading = true;
                
                const products = this.reception.products.map(p => {
                    return {
                        inventory_id: p.inventory_id,
                        product_id: p.product_id,
                        product_label: p.product_label,
                        quantity: p.quantity,
                        price: p.price,
                        unity: p.unity,
                        purchase_price: p.purchase_price,
                        vat: p.vat,
                        total: p.total,
                        sub_total: p.sub_total,
                        custom_product: p.custom_product,
                        discount: p.discount,
                        position: p.position
                    }
                })

                await helper.customPatch(`${process.env.VUE_APP_API}/receptions/${this.reception.id}`, {
                    reference: this.reception.reference,
                    date: this.reception.date,
                    supplier_id: this.reception.supplier_id,
                    purchase_id: this.reception.purchase_id,
                    notes: this.reception.notes,
                    status: this.reception.status,
                    products: products
                });

                this.loading = false;
                this.reorderMode = false;
                this.originalProductsOrder = null;
                
                this.$toast.open({
                    message: this.$t('updated_successfully'),
                    type: 'success',
                    position: 'bottom'
                });
                
                // Refresh data
                this.getReception(this.$route.params.id);
                
            } catch (error) {
                this.loading = false;
                this.$toast.open({
                    message: error.response?.data?.message || this.$t('error_occurred'),
                    type: 'error',
                    position: 'bottom'
                });
            }
        }
    },
    computed : {
        business(){ return this.$store.getters.BUSINESS},
        helper(){ return helper},
        total(){ return this.reception.products.length ? this.reception.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0},
        sub_total(){ return this.reception.products.length ? this.reception.products.map(o => o.sub_total).reduce((a, c) => { return a + c }) : 0}
    },
    watch : {
        editModalisOpen(newValue) {
            if(newValue === false){
                this.getReception(this.$route.params.id)
            }
        },
        step(newValue) {
            if(newValue === 'history' && this.reception.id){
                this.getReceptionHistory(this.reception.id)
            }
        }
    }
}
</script>

<style scoped>
    .product-rich-text::v-deep > :last-child {
        margin-bottom: 0 !important;
    }

    .table-borderless>:not(caption)>*>*{
        padding: 5px !important
    }
    
    .history-item {
        background-color: #f8f9fa;
        border-left: 4px solid #007bff !important;
    }
    
    .history-item:hover {
        background-color: #e9ecef;
    }
    
    .history-details {
        background-color: white;
        padding: 10px;
        border-radius: 5px;
        margin-top: 10px;
    }
    
    .badge {
        font-size: 0.75em;
    }
    
    .history-details .border-start {
        border-left: 2px solid #dee2e6 !important;
    }
    
    .history-details .border-start:hover {
        border-left-color: #007bff !important;
        background-color: #f8f9fa;
    }
    
    .history-details .small {
        font-size: 0.875em;
    }
    
    .history-details .ms-3 {
        margin-left: 1rem !important;
    }
</style>
